// Slow Burn: the second mode. Every row has one flame tile, and the letter you put there burns for
// the rest of the run; a burned letter can't be played, so each row costs exactly one new letter.
// A run's 26 flame columns and its gold letters are seeded by date, made ahead and stored. The run
// ends when no unplayed word avoids the ashes, or when the player puts it to bed. Words score as in
// the daily game. Ported from design/slow-burn/boards/rules.js, so a seed builds the same pattern.
import { LATEST_WORDS_VERSION, type WordList } from '@wbwl/words'
import {
  EDITION_TIME_ZONE, GOLD_TIERS, MAX_GOLD_RARE, MAX_GOLD_VOWELS, RARE_LETTERS, RULES_VERSION, SLOW_BURN, SLOW_BURN_FUSE, VOWELS, WORD_LENGTH,
} from './constants.ts'
import { editionDateAt, generateEdition, wordListFor, type GoldTier } from './edition.ts'
import { bit, indexOf, maskOf } from './letters.ts'
import { rngFromSeed } from './random.ts'
import { scoreWord, type WordScore } from './score.ts'

export interface SlowBurnEdition {
  /** Slow Burn No., counted in days from SLOW_BURN.launchDate (No. 1). null for random patterns. */
  readonly number: number | null
  readonly date: string | null
  readonly seed: string
  readonly rulesVersion: number
  readonly wordsVersion: number
  /** The flame tile's column (0–4) on each of the SLOW_BURN.rows rows. */
  readonly cols: readonly number[]
  readonly gold: Readonly<Record<string, GoldTier>>
}

/** How a run ended: no word fit, the player stopped, the endgame fuse ran out, or its day did (midnight Pacific). */
export type RunEnd = 'burned_out' | 'put_to_bed' | 'fuse_out' | 'day_over'

export interface RunState {
  readonly rows: readonly string[]
  /** One letter per row, in the order they burned. */
  readonly ashes: readonly string[]
  readonly ended: RunEnd | null
}

export type RunRejection =
  | { readonly reason: 'wrong_phase'; readonly phase: 'done' }
  | { readonly reason: 'wrong_length' }
  | { readonly reason: 'not_a_word' }
  | { readonly reason: 'already_played'; readonly row: number }
  | { readonly reason: 'burned_letter'; readonly letters: readonly string[] }

export type RunMoveResult =
  | { readonly ok: true; readonly state: RunState; readonly score: WordScore; readonly burned: string }
  | ({ readonly ok: false } & RunRejection)

export interface RunScore {
  readonly rows: readonly WordScore[]
  readonly points: number
  /** Rows played. */
  readonly depth: number
}

export interface BestRun {
  readonly depth: number
  readonly points: number
  readonly rows: readonly string[]
}

const DAY_MS = 86_400_000

/**
 * The pattern for a seed: each column takes 5 or 6 of the 26 rows, and none comes up three rows
 * running. Its own gold letters are for random patterns; dated runs use the daily edition's.
 */
export function generatePattern(seed: string): Pick<SlowBurnEdition, 'cols' | 'gold'> {
  const rnd = rngFromSeed(seed)
  let cols: number[]
  do {
    cols = []
    for (let c = 0; c < WORD_LENGTH; c++) for (let i = 0; i < 5; i++) cols.push(c)
    cols.push(Math.floor(rnd() * WORD_LENGTH))
    for (let i = cols.length - 1; i > 0; i--) {
      const j = Math.floor(rnd() * (i + 1))
      ;[cols[i], cols[j]] = [cols[j], cols[i]]
    }
  } while (cols.some((c, i) => i >= 2 && c === cols[i - 1] && c === cols[i - 2]))
  const gold: Record<string, GoldTier> = {}
  const isVowel = (l: string) => VOWELS.includes(l)
  const isRare = (l: string) => RARE_LETTERS.includes(l)
  for (const tier of GOLD_TIERS) {
    let letter: string
    do {
      letter = String.fromCharCode(65 + Math.floor(rnd() * 26))
    } while (
      gold[letter] ||
      (isVowel(letter) && Object.keys(gold).filter(isVowel).length >= MAX_GOLD_VOWELS) ||
      (isRare(letter) && Object.keys(gold).filter(isRare).length >= MAX_GOLD_RARE)
    )
    gold[letter] = tier
  }
  return { cols, gold }
}

/** Slow Burn No. for a date: No. 1 is SLOW_BURN.launchDate. */
export function slowBurnNumber(date: string): number {
  return Math.round((parseDate(date) - parseDate(SLOW_BURN.launchDate)) / DAY_MS) + 1
}

export function slowBurnDate(number: number): string {
  if (!Number.isInteger(number) || number < 1) throw new RangeError(`Not a Slow Burn number: ${number}`)
  return new Date(parseDate(SLOW_BURN.launchDate) + (number - 1) * DAY_MS).toISOString().slice(0, 10)
}

/** Today's Slow Burn date at an instant (it turns over with the daily edition, midnight Pacific). */
export const slowBurnDateAt = (instant: Date = new Date(), timeZone: string = EDITION_TIME_ZONE): string => editionDateAt(instant, timeZone)

/**
 * The run for a date. Its seed is `slow-burn-<date>`, so every day has a new burn order. Its gold
 * letters are the daily edition's for that date, so both games share the day's bonus letters: pass
 * the stored edition's (a re-rolled edition can differ from the date's first seed), or by default
 * they come from generateEdition(date).
 */
export function generateSlowBurn(date: string, options: { gold?: Readonly<Record<string, GoldTier>> } = {}): SlowBurnEdition {
  const number = slowBurnNumber(date)
  if (number < 1) throw new RangeError(`${date} is before Slow Burn No. 1 (${SLOW_BURN.launchDate})`)
  const seed = `slow-burn-${date}`
  const gold = options.gold ?? generateEdition(date).gold
  return { number, date, seed, rulesVersion: RULES_VERSION, wordsVersion: LATEST_WORDS_VERSION, cols: generatePattern(seed).cols, gold: { ...gold } }
}

/** An unnumbered pattern from any seed, for tests and tools. */
export function randomSlowBurn(seed: string): SlowBurnEdition {
  return { number: null, date: null, seed, rulesVersion: RULES_VERSION, wordsVersion: LATEST_WORDS_VERSION, ...generatePattern(seed) }
}

export const newRun = (): RunState => ({ rows: [], ashes: [], ended: null })

const normalize = (word: string) => word.trim().toUpperCase()

/** Whether any unplayed word avoids the ashes. */
export function hasRunMove(state: Pick<RunState, 'rows' | 'ashes'>, words: WordList): boolean {
  const burned = maskOf(state.ashes)
  const index = indexOf(words)
  for (let i = 0; i < index.words.length; i++) if (!(index.masks[i] & burned) && !state.rows.includes(index.words[i])) return true
  return false
}

/** How many unplayed words avoid the ashes: the run's "words left". */
export function runWordsLeft(state: Pick<RunState, 'rows' | 'ashes'>, words: WordList): number {
  const burned = maskOf(state.ashes)
  const index = indexOf(words)
  let n = 0
  for (let i = 0; i < index.words.length; i++) if (!(index.masks[i] & burned) && !state.rows.includes(index.words[i])) n++
  return n
}

/** Unplayed words that avoid the ashes, in word-list order. */
export function runLegalWords(state: Pick<RunState, 'rows' | 'ashes'>, words: WordList): string[] {
  const burned = maskOf(state.ashes)
  const index = indexOf(words)
  return index.words.filter((w, i) => !(index.masks[i] & burned) && !state.rows.includes(w))
}

/**
 * Plays a word on the next row and burns the letter on its flame tile. Checks run in this order:
 * wrong_length, not_a_word, already_played, burned_letter. When no word is left (or every row is
 * played), the run ends as burned_out.
 */
export function applyRunMove(edition: SlowBurnEdition, state: RunState, word: string, words: WordList = wordListFor(edition)): RunMoveResult {
  if (state.ended) return { ok: false, reason: 'wrong_phase', phase: 'done' }
  const w = normalize(word)
  if (w.length !== WORD_LENGTH) return { ok: false, reason: 'wrong_length' }
  if (!words.has(w)) return { ok: false, reason: 'not_a_word' }
  const playedAt = state.rows.indexOf(w)
  if (playedAt >= 0) return { ok: false, reason: 'already_played', row: playedAt }
  const blocked = [...new Set([...w].filter((l) => state.ashes.includes(l)))]
  if (blocked.length) return { ok: false, reason: 'burned_letter', letters: blocked }
  const burned = w[edition.cols[state.rows.length]]
  const next: RunState = { rows: [...state.rows, w], ashes: [...state.ashes, burned], ended: null }
  const over = next.rows.length >= edition.cols.length || !hasRunMove(next, words)
  return { ok: true, state: over ? { ...next, ended: 'burned_out' } : next, score: scoreWord(w, edition), burned }
}

/**
 * Ends the run early: the player stopping (put_to_bed), the fuse running out (fuse_out) or the day
 * ending (day_over). A run needs at least one row first, so an empty run never reaches the board.
 */
export function endRun(state: RunState, how: Exclude<RunEnd, 'burned_out'> = 'put_to_bed'): { readonly ok: true; readonly state: RunState } | { readonly ok: false; readonly reason: 'wrong_phase'; readonly phase: 'play' | 'done' } {
  if (state.ended) return { ok: false, reason: 'wrong_phase', phase: 'done' }
  if (state.rows.length === 0) return { ok: false, reason: 'wrong_phase', phase: 'play' }
  return { ok: true, state: { ...state, ended: how } }
}

/** Whether the endgame fuse is lit for the row up next: the run is going and fewer than SLOW_BURN_FUSE.wordsLeft words fit. */
export const fuseLit = (state: RunState, words: WordList): boolean =>
  !state.ended && state.rows.length > 0 && runWordsLeft(state, words) < SLOW_BURN_FUSE.wordsLeft

export type RunReplay =
  | { readonly ok: true; readonly state: RunState }
  | { readonly ok: false; readonly at: number; readonly rejection: RunRejection }

/** Rebuilds a run from stored rows; `ended` ends it early (put to bed, fuse out) if it hadn't burned out. */
export function replayRun(edition: SlowBurnEdition, rows: readonly string[], ended: Exclude<RunEnd, 'burned_out'> | null = null, words: WordList = wordListFor(edition)): RunReplay {
  let state = newRun()
  for (const [i, word] of rows.entries()) {
    const r = applyRunMove(edition, state, word, words)
    if (!r.ok) return { ok: false, at: i, rejection: r }
    state = r.state
  }
  if (ended && !state.ended && state.rows.length) state = { ...state, ended }
  return { ok: true, state }
}

export function scoreRun(edition: Pick<SlowBurnEdition, 'gold' | 'rulesVersion'>, state: Pick<RunState, 'rows'>): RunScore {
  const rows = state.rows.map((w) => scoreWord(w, edition))
  return { rows, points: rows.reduce((s, r) => s + r.total, 0), depth: rows.length }
}

/** The scoreboard's order: more points first, then deeper. */
export const compareRuns = (a: Pick<RunScore, 'depth' | 'points'>, b: Pick<RunScore, 'depth' | 'points'>): number =>
  b.points - a.points || b.depth - a.depth

/**
 * Beam search for the run with the most points (then the deepest). A row's word only decides which letter
 * burns (the one on the flame) and the points, so each state expands into at most one child per
 * letter: the best-scoring unplayed word with that letter on the flame. States are ranked by how
 * many words their ashes still allow, plus `pointsWeight` × points per row so far.
 */
export function solveSlowBurn(edition: SlowBurnEdition, options: { beam?: number; pointsWeight?: number } = {}, words: WordList = wordListFor(edition)): BestRun {
  const beam = options.beam ?? SLOW_BURN.solverBeam
  // Over four sample patterns, 6 found 25–180 more points than 0.6 at the same speed.
  const pointsWeight = options.pointsWeight ?? 6
  const index = indexOf(words)
  const scores = index.words.map((w) => scoreWord(w, edition).total)
  const allowed = new Map<number, number>()
  const allowedBy = (mask: number) => {
    let n = allowed.get(mask)
    if (n === undefined) {
      n = 0
      for (let i = 0; i < index.masks.length; i++) if (!(index.masks[i] & mask)) n++
      allowed.set(mask, n)
    }
    return n
  }
  interface Node { mask: number; rows: string[]; points: number }
  let states: Node[] = [{ mask: 0, rows: [], points: 0 }]
  let best: Node = states[0]
  for (let r = 0; r < edition.cols.length; r++) {
    const col = edition.cols[r]
    const children = new Map<number, Node>()
    for (const s of states) {
      const pick = new Map<string, number>()
      for (let i = 0; i < index.words.length; i++) {
        if (index.masks[i] & s.mask) continue
        const w = index.words[i]
        if (s.rows.includes(w)) continue
        const cur = pick.get(w[col])
        if (cur === undefined || scores[i] > scores[cur]) pick.set(w[col], i)
      }
      for (const [letter, i] of pick) {
        const mask = s.mask | bit(letter)
        const child = { mask, rows: [...s.rows, index.words[i]], points: s.points + scores[i] }
        const prev = children.get(mask)
        if (!prev || child.points > prev.points) children.set(mask, child)
      }
    }
    if (!children.size) break
    const all = [...children.values()]
    for (const c of all) if (c.points > best.points || (c.points === best.points && c.rows.length > best.rows.length)) best = c
    states = all
      .map((c) => ({ c, key: Math.log(1 + allowedBy(c.mask)) * 100 + (c.points * pointsWeight) / (r + 1) }))
      .sort((a, b) => b.key - a.key || (a.c.rows.join() < b.c.rows.join() ? -1 : 1))
      .slice(0, beam)
      .map((x) => x.c)
  }
  return { depth: best.rows.length, points: best.points, rows: best.rows }
}

function parseDate(date: string): number {
  const ms = /^\d{4}-\d{2}-\d{2}$/.test(date) ? Date.parse(`${date}T00:00:00Z`) : NaN
  if (Number.isNaN(ms) || new Date(ms).toISOString().slice(0, 10) !== date) throw new RangeError(`Not a YYYY-MM-DD date: ${date}`)
  return ms
}
